import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, treatsSeveral, tweenCalls } from '../skillUsage';

const HIDES_TO_ZERO = /(?:opacity|autoAlpha):\s*0\s*[,}]/;
const SHOWS_TO_ONE = /(?:opacity|autoAlpha):\s*1\s*[,}]/;

function handsOffBetweenItems(script: string): boolean {
    let exits = 0;
    let entrances = 0;
    for (const { method, chunk } of tweenCalls(script)) {
        // A fromTo's first object is where it starts; only its second says where it ends.
        const endState = method === 'fromTo' ? chunk.slice(chunk.indexOf('}') + 1) : chunk;
        if (method === 'from') {
            entrances += HIDES_TO_ZERO.test(chunk) ? 1 : 0;
        } else if (HIDES_TO_ZERO.test(endState)) {
            exits += 1;
        } else if (SHOWS_TO_ONE.test(endState)) {
            entrances += 1;
        }
    }
    // A field recoloured in place carries no opacity, so a looped takeover of that kind shows a
    // single entrance call — the word's — for every item.
    return exits >= 1 && (entrances >= 2 || (entrances >= 1 && treatsSeveral(script)));
}

function isFullBleed(declarations: string): boolean {
    return (
        /inset:\s*0/.test(declarations) ||
        (/width:\s*100%/.test(declarations) && /height:\s*100%/.test(declarations)) ||
        ['top', 'right', 'bottom', 'left'].every((edge) =>
            new RegExp(`\\b${edge}:\\s*0`).test(declarations),
        )
    );
}

/**
 * Layout and colour often live apart — the stack's geometry in one stylesheet rule, each field's
 * colour inline — so an element's declarations are its inline style plus the rules for its classes.
 */
function changesTheField(script: string, markup: string): boolean {
    const classRules = new Map<string, string>();
    for (const [, name, body] of markup.matchAll(/\.([\w-]+)\s*\{([^}]*)\}/g)) {
        classRules.set(name, `${classRules.get(name) ?? ''};${body}`);
    }
    let fullBleedFields = 0;
    for (const [, attributes] of markup.matchAll(/<\w+\b([^>]*)>/g)) {
        const inline = /\bstyle=(["'])(.*?)\1/.exec(attributes)?.[2] ?? '';
        const classes = /\bclass=(["'])(.*?)\1/.exec(attributes)?.[2].split(/\s+/) ?? [];
        const declarations = [inline, ...classes.map((name) => classRules.get(name) ?? '')].join(
            ';',
        );
        if (/background/.test(declarations) && isFullBleed(declarations)) {
            fullBleedFields += 1;
        }
    }
    return fullBleedFields >= 2 || /background(?:Color)?:/.test(script);
}

export const boldListTakeoverSkill: CodeoSkill = {
    name: SKILL_NAMES.boldListTakeover,
    kind: 'codeo',
    description:
        'Turn a short list of one- or two-word items into a run of full-frame takeovers — each item alone, ' +
        'oversized, on its own bold colour field, handing off to the next — instead of a bulleted ' +
        'slide. For "engage, support, invest": a list the viewer should feel one beat at a time.',
    checkLooksFor:
        'list items that each enter and hand off to the next, over a full-bleed colour field that ' +
        'changes between them',
    documentUsesSkill: check(
        (script, markup) => handsOffBetweenItems(script) && changesTheField(script, markup),
    ),
    instructions: `# Bold List Takeover

A short list told the way a bullet slide never can: every item gets the whole frame to itself. One word, set huge and heavy, centred on a full-bleed colour field; then it clears, the field shifts, and the next word takes over. Three items become three beats instead of one slide the viewer reads ahead of the voiceover.

The field is what makes it a takeover rather than a word swap. Each item owns a colour, and the colours step along one hue — deep to bright — so the list visibly builds as it goes. The colour shift is also the transition: it covers the instant the frame holds no word, so the handoff reads as one continuous move.

Reach for it when the copy is a list of two to five items of one or two words each, and the list is the point of the scene. Use \`${SKILL_NAMES.staggeredListReveal}\` when the items should be seen together as a group, \`${SKILL_NAMES.wordSwapInPlace}\` when a sentence stays and only one word in it changes, and \`${SKILL_NAMES.anchoredRelayReveal}\` when the items are claims about a subject that stays on screen.

## Markup

One field per item, stacked full-bleed in item order, each a darkening gradient over the same palette colour; only the first is visible from frame zero. Above them, one centring box per item holding that item's word, so every word lands on the same centre however long it is.

\`\`\`html
<div id="blt-stage" style="position: absolute; inset: 0; overflow: hidden;">
  <div class="blt-field" style="position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0, 0, 0, 0.78), rgba(0, 0, 0, 0.6)), var(--l5-color-2, #0a66ff);"></div>
  <div class="blt-field" style="position: absolute; inset: 0; opacity: 0; background: linear-gradient(180deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.32)), var(--l5-color-2, #0a66ff);"></div>
  <div class="blt-field" style="position: absolute; inset: 0; opacity: 0; background: linear-gradient(180deg, rgba(0, 0, 0, 0.32), rgba(0, 0, 0, 0.12)), var(--l5-color-2, #0a66ff);"></div>
  <div class="blt-slot" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;">
    <div class="blt-word" id="blt-word-engage" style="font-family: var(--l5-font-0), sans-serif; font-size: 260px; font-weight: 900; letter-spacing: -0.03em; line-height: 1; color: #ffffff;">Engage</div>
  </div>
  <div class="blt-slot" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;">
    <div class="blt-word" id="blt-word-support" style="font-family: var(--l5-font-0), sans-serif; font-size: 260px; font-weight: 900; letter-spacing: -0.03em; line-height: 1; color: #ffffff;">Support</div>
  </div>
  <div class="blt-slot" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;">
    <div class="blt-word" id="blt-word-invest" style="font-family: var(--l5-font-0), sans-serif; font-size: 260px; font-weight: 900; letter-spacing: -0.03em; line-height: 1; color: #ffffff;">Invest</div>
  </div>
</div>
\`\`\`

## Timeline code

\`LANDS\` is the second each item takes the frame. The outgoing word clears just before the next lands — two words never share the centre — and the next field fades up across that handoff.

\`\`\`js
const tl = gsap.timeline({ paused: true });

const SCENE_SECONDS = 6.4;
const LANDS = [0.4, 2.2, 4.0];
const CLEAR = 0.3;
const GAP = 0.05;

const words = gsap.utils.toArray('.blt-word');
const fields = gsap.utils.toArray('.blt-field');

words.forEach((word, index) => {
    const at = LANDS[index];
    if (index > 0) {
        tl.to(fields[index], { opacity: 1, duration: 0.6, ease: 'power2.inOut' }, at - CLEAR - GAP);
    }
    tl.fromTo(word,
        { opacity: 0, y: 90, filter: 'blur(14px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.55, ease: 'power3.out' },
        at);
    const next = LANDS[index + 1];
    if (next !== undefined) {
        tl.to(word, { opacity: 0, y: -90, filter: 'blur(14px)', duration: CLEAR, ease: 'power2.in' }, next - CLEAR - GAP);
    }
});

// Holds the last item on screen; without it the timeline ends the moment that word lands.
tl.set({}, {}, SCENE_SECONDS);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **On narration**, \`LANDS\` are the items' spoken onsets, read from the scene's \`cues:\` line. The takeover is the voice made visible; an item that changes a beat off its word reads as a slideshow. Onsets under 0.9s apart are too fast to take over: the entrance is still running when the exit starts, so two words share the centre. A list spoken that quickly wants \`${SKILL_NAMES.staggeredListReveal}\` instead.
- **Spacing**: 1.4–2s between items without narration. A single word reads in well under a second, so the rest of each beat is the item landing as a beat — any shorter and the list blurs into a flicker.
- **The last item** is usually the one the list builds to; give it the brightest field and at least 1.5s of hold.
- **Field ramp**: step the darkening overlay down by roughly the same amount per item so the brightening is steady. A second palette colour on the last field reads as a payoff; a different colour on every field reads as a set of unrelated cards.
- **Wipe instead of fade**: reveal each field from below with \`clipPath: 'inset(100% 0 0 0)'\` → \`'inset(0% 0 0 0)'\` (set the hidden state with \`gsap.set\`, not CSS) for a heavier, more physical change. Keep it moving upward, with the words.
- **Lead-in line**: when the list needs its frame ("A ten-year plan to"), give the lead-in its own beat before the first takeover rather than parking it above every word.
- **Two-word items** are fine ("Build trust") — drop the size until the item fits on one line with clear margin. Three words is a phrase, not a list item.

## Pitfalls

- **The brightest field is where contrast fails.** If it fires there, deepen that field's overlay — don't switch the word to a dark colour mid-list, which breaks the run.
- **Fields stack in item order** and only ever fade in. Fading earlier fields out exposes the frame behind them mid-handoff.
- **Five items at most.** Past that, the takeover rhythm turns into a strobe; group the list or pick the items that matter.
- **Not for sentences.** An item the viewer has to read rather than glance at belongs in a layout, not a takeover.`,
};
